import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import { newsApi } from '../services/api';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

export default function NewsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    newsApi
      .getDetail(id)
      .then((data) => setArticle(data))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 16 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!article) {
    return (
      <Container maxWidth="md" sx={{ py: 16, textAlign: 'center' }}>
        <Typography variant="h5" color="text.secondary">资讯不存在或已删除</Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Container maxWidth="md">
        {/* 面包屑 */}
        <Breadcrumbs sx={{ mb: 4 }}>
          <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            首页
          </Link>
          <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/news')}
          >
            新闻资讯
          </Link>
          <Typography color="text.primary" noWrap sx={{ maxWidth: 200 }}>
            {article.title}
          </Typography>
        </Breadcrumbs>

        {/* 文章标题 */}
        <Typography variant="h3" sx={{ mb: 3, fontWeight: 700, lineHeight: 1.4 }}>
          {article.title}
        </Typography>

        {/* 文章元信息 */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4, flexWrap: 'wrap' }}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
            <CalendarTodayIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2">
              {formatDate(article.publishedAt || article.createdAt)}
            </Typography>
          </Stack>
          {article.tags &&
            article.tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" variant="outlined" />
            ))}
        </Stack>

        {/* 封面图 */}
        {article.coverImageInfo?.url && (
          <Box
            component="img"
            src={article.coverImageInfo.url}
            alt={article.title}
            sx={{
              width: '100%',
              maxHeight: 400,
              objectFit: 'cover',
              borderRadius: 2,
              mb: 4,
            }}
          />
        )}

        {/* 摘要 */}
        {article.summary && (
          <Box
            sx={{
              bgcolor: 'grey.50',
              borderLeft: 4,
              borderColor: 'primary.main',
              p: 3,
              borderRadius: '0 8px 8px 0',
              mb: 4,
            }}
          >
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              {article.summary}
            </Typography>
          </Box>
        )}

        {/* 正文 */}
        <Box
          sx={{
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 1,
              my: 2,
            },
            '& p': {
              lineHeight: 2,
              mb: 2,
              fontSize: '1.05rem',
              color: 'text.primary',
            },
            '& h1, & h2, & h3': {
              mt: 4,
              mb: 2,
              fontWeight: 600,
            },
            '& a': {
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            },
            '& blockquote': {
              borderLeft: 4,
              borderColor: 'grey.300',
              pl: 2,
              ml: 0,
              color: 'text.secondary',
              fontStyle: 'italic',
            },
          }}
          dangerouslySetInnerHTML={{ __html: article.content }}
        />
      </Container>
    </Box>
  );
}
